import { ItemTypes } from "game-common/item/item_type"
import { ItemTypeMeta } from "../item/item"

import { Coordinate, Location, MapSpriteCap, WeaponType } from "../models"
import { EntityTileCharacteristics, EntityTileTypes } from "../item/item_entity_tiles"
import type { GrassKind, TreeKind } from "../content/schema"

export type OreMaterial = "coal" | "iron" | "copper" | "silver" | "gold"

export interface MapDelta {
    x: number
    y: number
    blocking?: boolean
    occlusion?: boolean
    tileId?: string
    removed?: boolean
    layerId?: string
    surfaceId?: string
    mapSpriteCap?: MapSpriteCap
    temporary?: boolean
    collideable?: boolean
    context?: {
        wallFaction?: string
        tileInteractions?: import("../entity_tile_interactions").TileInteraction[]
        parent?: Coordinate
        surfaceId?: string
        itemType?: ItemTypes
        hatch?: import("../room/hatch").HatchView | null
        /** Transient visual cue; the broken hatch itself is already absent and passable. */
        hatchBrokenAt?: number | null
        stairLanding?: Location
        /** Directional connections for a continuous ladder shaft sharing one cell. */
        stairLandings?: Partial<Record<"up" | "down", Location>>
        stairExit?: Location
        context?: { stairLanding?: Location; stairLandings?: Partial<Record<"up" | "down", Location>>; stairExit?: Location; buildingId?: string }
        hp?: number
        hpMax?: number
        highZ?: boolean
        previewTarget?: boolean
        ladderExtension?: boolean
        bonecallerShrineId?: string
        bonecallerStoneIndex?: number
        /** Visible mineral kind; reward quantities remain in server campaign state. */
        oreMaterial?: OreMaterial
    }
}

export type TileEffectType = "damage" | "destroy" | "remove" | "overlay" | "repair" | "arrow-impact" | "arrow-broken"

export interface TileEffect {
    /** "arrow-impact" / "arrow-broken": the arrow that came down, so its shooter's client, which already drew it, can skip it. */
    arrowId?: string
    impactPosition?: Coordinate
    impactAngle?: number
    /** "arrow-broken": the broken arrow's slant out of the ground, degrees (0 lies flat), as a lasting one is posed. */
    impactPitch?: number
    /** "arrow-broken": struck into a cliff's face, how far up it, as its flight was drawn: it sticks there as it flew. */
    impactLift?: number
    /** The room the tile is in, when it is another storey of the receiver's building, drawn around their own. */
    roomId?: string
    // World-space source of a strike, also used to reconcile local contact feedback.
    strikeFrom?: Coordinate
    strikerId?: string
    strikeStrength?: number
    layerId?: string
    mapRoughCoordinates: Coordinate
    effectType: TileEffectType
    surfaceId?: string
    tileId?: string
    overlayTileId?: string
    entityTile?: EntityTile
    hpPct?: number
    hp?: number
    hpMax?: number
}

export type EntityTile = (typeof EntityTileTypes)[number]

// Trees are tagged in their tile definition (`tree` in content/game/entities/tiles.json), so a new
// tree -- a Sprite Lab one included -- needs no code change and is never recognised by its name.
const treeKind = (tile?: string): TreeKind | undefined =>
    tile ? EntityTileCharacteristics[tile]?.tree : undefined

/** A full-grown tree: chopped for wood, fades while it hides the player, shows trunk damage. */
export const isTreeEntityTile = (tile?: EntityTile): boolean => treeKind(tile) === "grown"

/** Any tree, grown or a young growth stage (never a sapling): what maps and forest density count. */
export const isAnyTreeEntityTile = (tile?: string): boolean => !!treeKind(tile)

/** Every full-grown tree's entity tile id, for scans that take a list of ids. */
export const treeEntityTiles = (): EntityTile[] => EntityTileTypes.filter(tile => isTreeEntityTile(tile))

// Long grass is tagged the same way (`grass` in tiles.json): the old LPC `tallGrass` and the ember
// meadow's Lab-made clumps alike, so wind, wildlife cover and the cut sound never go by name.
/** Which kind of grass an entity tile is, if it is grass: "long", or "sprout" (long grass's young shoot). */
export const grassKind = (tile?: string): GrassKind | undefined =>
    tile ? EntityTileCharacteristics[tile]?.grass : undefined

/** Long grass: bends in the wind, is cover for wildlife, is cut rather than chopped. */
export const isLongGrassEntityTile = (tile?: string): boolean => grassKind(tile) === "long"

/** Any grass, long or a young shoot: cut, not chopped -- no thunk of an axe in wood. */
export const isGrassEntityTile = (tile?: string): boolean => !!grassKind(tile)

// The mineable rock blocks. Like a tree, each is drawn TALLER than the cell it blocks -- the art
// reaches a row above its foot (see fillEmberCaveOre, which keeps that row clear) -- so a block sitting
// one row south of the player covers the player's body. That is why the client's occluder-fade treats
// them as candidates alongside trees.
export const isOreEntityTile = (tile: EntityTile) => ["ore-coal", "testore"].includes(tile)

interface GrowthDescriptor {
    entityTileId: EntityTile
    requiredTime: number
}

interface ItemCreationDescriptor {
    itemType: ItemTypeMeta
    quantity: number
    replicate?: boolean
    probability?: number
}

export interface EntityTileMeta {
    hp: number
    interactive?: boolean
    growthSequence?: GrowthDescriptor[]
    onDestructionCreateItems?: ItemCreationDescriptor[]
    tree?: TreeKind
    grass?: GrassKind
}

export type EntityTileDamageMeta = {
    weaponType: WeaponType
    damage: number
}
